import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams, NavLink } from "react-router-dom";
import { msmartAxios } from "../../../api/axios";
import toast from "react-hot-toast";
import { useAuthContext } from "../../../hooks/useAuthContext";
import LeadFormModal from "../LeadFormModal";
import ViewSubmissionsModal from "../ViewSubmissionsModal";
import EditLeadFormModal from "../EditLeadFormModal";
import { getErrorMessage } from "../../../utils/errorMessage";

function ManagerLeadFormBuilder() {
  const { user } = useAuthContext();
  const { teamId } = useParams();

  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);

  const [showSubmissions, setShowSubmissions] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState(null);

  const [showEdit, setShowEdit] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);

  const [search, setSearch] = useState("");

  const EASYBORANG = process.env.REACT_APP_EASYBORANG || "";

  const fetchForms = useCallback(async () => {
    if (!user?.token) return;
    setLoading(true);
    try {
      const res = await msmartAxios.get(`/api/msmart/lead-forms/${teamId}`, {
        headers: { accessToken: user.token },
      });
      setForms(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to fetch forms"));
      setForms([]);
    } finally {
      setLoading(false);
    }
  }, [teamId, user?.token]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const filteredForms = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = [...forms];

    // sort: latest first
    list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    if (!q) return list;

    return list.filter((f) => {
      const title = String(f.formTitle || "").toLowerCase();
      const url = String(f.customUrl || "").toLowerCase();
      return title.includes(q) || url.includes(q);
    });
  }, [forms, search]);

  const copyLink = async (customUrl) => {
    const fullUrl = `${EASYBORANG}${customUrl}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success("Link copied!");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to copy link"));
    }
  };

  const handleSubmit = async ({
    formTitle,
    formDescription,
    formTheme,
    customUrl,
    formImageFile,
    selectedFields,
    enabledThankYouPage,
    thankYouPageMessage,
    enabledRedirect,
    redirectUrl,
    isAutoRedirect,
    faceBookPixel,
    tikTokPixel,
    googlePixel,
  }) => {
    if (!formTitle || !customUrl || selectedFields.length === 0) {
      return toast.error("Please complete all fields.");
    }

    const payload = new FormData();
    payload.append("teamId", teamId);
    payload.append("formTitle", formTitle);
    payload.append("formDescription", formDescription);
    payload.append("formTheme", formTheme);
    payload.append("customUrl", customUrl);

    payload.append("thankYouEnabled", enabledThankYouPage);
    payload.append("thankYouPageMessage", thankYouPageMessage);
    payload.append("redirectEnabled", enabledRedirect);
    payload.append("redirectUrl", redirectUrl);
    payload.append("autoRedirect", isAutoRedirect);

    payload.append("pixelMeta", faceBookPixel);
    payload.append("pixelTiktok", tikTokPixel);
    payload.append("pixelGoogleAds", googlePixel);

    payload.append("formConfig", JSON.stringify(selectedFields));
    if (formImageFile) payload.append("formImage", formImageFile);

    try {
      await msmartAxios.post("/api/msmart/lead-form", payload, {
        headers: {
          "Content-Type": "multipart/form-data",
          accessToken: user.token,
        },
      });

      toast.success("Form saved!");
      setShowModal(false);
      await fetchForms(); // ✅ refresh without reload
    } catch (err) {
      let message = getErrorMessage(err, "Upload failed");
      if (
        typeof err?.response?.data === "string" &&
        err.response.data.includes("MulterError: File too large")
      )
        message = "Image too large. Max size is 4MB.";

      toast.error(message);
    }
  };

  const handleUpdateForm = async (id, updatedData) => {
    try {
      const payload = new FormData();

      for (const key in updatedData) {
        if (key === "selectedFields") {
          payload.append("formConfig", JSON.stringify(updatedData[key]));
        } else if (key === "formImageFile" && updatedData[key]) {
          payload.append("formImage", updatedData[key]);
        } else {
          payload.append(key, updatedData[key]);
        }
      }

      await msmartAxios.put(`/api/msmart/lead-form/${id}`, payload, {
        headers: {
          "Content-Type": "multipart/form-data",
          accessToken: user.token,
        },
      });

      toast.success("Form updated!");
      setShowEdit(false);
      setSelectedForm(null);
      await fetchForms(); // ✅ refresh list
    } catch (err) {
      let message = getErrorMessage(err, "Upload failed");
      if (
        typeof err?.response?.data === "string" &&
        err.response.data.includes("MulterError: File too large")
      )
        message = "Image too large. Max size is 4MB.";
      else if (
        typeof err?.response?.data === "string" &&
        err.response.data.includes("Only .jpg, .png, .webp allowed")
      )
        message = "Invalid file type. Only JPG, PNG, and WebP are allowed.";

      toast.error(message);
    }
  };

  return (
    <div className="container py-6 ss-page">
      <div className="row text-start">
        <div className="col-lg-12">
        <div>
            <h1 className="header-title mb-1">M-Smart</h1>
            <div className="text-muted" style={{ fontSize: 14 }}>
            Sales Activity Management.
            </div>
          </div>
        </div>
      </div>

      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="msmart-segment my-4">
            <NavLink
              to={`/msmart/team/summary/${teamId}`}
              className={({ isActive }) =>
                `msmart-seg-link ${isActive ? "active" : ""}`
              }
            >
              Team
            </NavLink>

            <NavLink
                 to={'#'}// <-- route personal kau
              className={({ isActive }) =>
                `msmart-seg-link ${isActive ? "active" : ""}`
              }
            >
              Personal
            </NavLink>
          </div>
        </div>
      </div>

      <div className="card mt-4 ds-card">
        <div className="card-header">
          <ul className="nav nav-tabs card-header-tabs">
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/manager/database/${teamId}`}>
                Manage
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/manager/followup/${teamId}`}>
                Follow Up
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/manager/summary/${teamId}`}>
                Summary
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/manager/summary/${teamId}?view=analysis`}>Analysis</Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link active" to="#">
                Form
              </Link>
            </li>
          </ul>
        </div>

        <div className="card-body">
          {/* Toolbar */}
          <div className="d-flex flex-column flex-md-row gap-2 justify-content-between align-items-md-center mb-3">
            <div className="d-flex flex-column flex-md-row gap-2 flex-grow-1">
              <div className="form-floating flex-grow-1">
                <input
                  id="formSearch"
                  className="form-control"
                  placeholder="Search forms"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <label htmlFor="formSearch">Search (title / url)</label>
              </div>

              <div className="d-flex gap-2 align-items-center">
                <span className="ds-chip">
                  <i className="bi bi-ui-checks"></i> Total: <b>{filteredForms.length}</b>
                </span>

                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={fetchForms}
                  disabled={loading}
                  title="Refresh"
                >
                  <i className="bi bi-arrow-clockwise"></i>
                </button>
              </div>
            </div>

            <div className="text-end">
              <button
                onClick={() => setShowModal(true)}
                className="btn btn-dark"
              >
                <i className="bi bi-plus-lg me-1"></i> Create Form
              </button>
            </div>
          </div>

          {/* List */}
          {loading ? (
            <div className="mt-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="card mb-3">
                  <div className="card-body">
                    <div className="skeleton-box" style={{ height: 16, width: "40%" }} />
                    <div className="skeleton-box mt-2" style={{ height: 12, width: "65%" }} />
                    <div className="skeleton-box mt-3" style={{ height: 34, width: 220, borderRadius: 14 }} />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredForms.length === 0 ? (
            <p className="text-muted mt-3">No forms created yet.</p>
          ) : (
            <div className="mt-3">
              {filteredForms.map((form) => {
                const fullUrl = `${EASYBORANG}${form.customUrl}`;
                return (
                  <div key={form.id} className="card mb-3 card-interactive">
                    <div className="card-body">
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-2">
                        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                          <div className="kpi-icon">
                            <i className="bi bi-file-earmark-text"></i>
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <h5 className="mb-0 text-truncate">{form.formTitle}</h5>
                            <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                              Created:{" "}
                              {new Date(form.createdAt).toLocaleString("en-GB", {
                                dateStyle: "medium",
                                timeStyle: "short",
                                timeZone: "Asia/Kuala_Lumpur",
                              })}
                            </div>
                          </div>
                        </div>

                        <div className="d-flex gap-2 justify-content-md-end">
                          <a
                            href={fullUrl}
                            className="btn btn-sm btn-outline-dark"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <i className="bi bi-box-arrow-up-right me-1"></i> View
                          </a>

                          <button
                            className="btn btn-sm btn-outline-dark"
                            onClick={() => {
                              setSelectedForm(form);
                              setShowEdit(true);
                            }}
                          >
                            <i className="bi bi-pencil me-1"></i> Edit
                          </button>

                          <button
                            className="btn btn-sm btn-dark"
                            onClick={() => {
                              setSelectedFormId(form.id);
                              setShowSubmissions(true);
                            }}
                          >
                            <i className="bi bi-table me-1"></i> Submissions
                          </button>
                        </div>
                      </div>

                      <div className="d-flex flex-column flex-md-row gap-2 align-items-md-center justify-content-between">
                        <div className="text-muted" style={{ fontSize: "0.95rem", overflowWrap: "anywhere" }}>
                          <span
                            className="leadform-link"
                            onClick={() => copyLink(form.customUrl)}
                            title="Click to copy"
                          >
                            {fullUrl} <i className="bi bi-clipboard ms-1"></i>
                          </span>
                        </div>

                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => copyLink(form.customUrl)}
                          >
                            <i className="bi bi-copy me-1"></i> Copy Link
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Create */}
      <LeadFormModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSubmit={handleSubmit}
      />

      {/* Submissions */}
      <ViewSubmissionsModal
        show={showSubmissions}
        onHide={() => setShowSubmissions(false)}
        formId={selectedFormId}
        token={user.token}
        teamId={teamId}
      />

      {/* Edit */}
      <EditLeadFormModal
        show={showEdit}
        onHide={() => setShowEdit(false)}
        formData={selectedForm}
        onUpdate={handleUpdateForm}
      />
    </div>
  );
}

export default ManagerLeadFormBuilder;
